/**
 * Undoing a "filed" record that has no ARN.
 * @file src/web/features/admin/components/FiledClaimActions.tsx
 *
 * Shown beside a history row marked filed without an ARN. The person asks the
 * portal (read-only); only if it says "not filed" is the reset offered — and
 * the server asks again when it is pressed, so this screen's memory of the
 * answer is a convenience, not the authority. Nothing is deleted.
 */
import { useState } from 'react';
import { Loader2, RefreshCw, Undo2 } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { adminFetch } from '../lib/admin-api';

type Check = { state: 'no_session' | 'unknown' | 'not_filed' | 'filed'; reason?: string; arn?: string | null; filedOn?: string | null; checkedAt?: string };

export function FiledClaimActions({ filingId, entityId, onDone }: { filingId: string; entityId: string | null; onDone: () => void }) {
  const [busy, setBusy] = useState<'check' | 'reset' | null>(null);
  const [check, setCheck] = useState<Check | null>(null);
  const q = { entityId: entityId ?? undefined };

  const ask = async () => {
    setBusy('check'); setCheck(null);
    try {
      const res = await adminFetch(`/api/gst/file/${filingId}/portal-check`, { method: 'POST', query: q });
      const b = await res.json().catch(() => ({}));
      if (!res.ok || b.ok === false) throw new Error(b?.error || 'Could not ask the portal');
      setCheck(b);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not ask the portal'); }
    finally { setBusy(null); }
  };

  const reset = async () => {
    setBusy('reset');
    try {
      const res = await adminFetch(`/api/gst/file/${filingId}/reset-to-prepared`, { method: 'POST', query: q });
      const b = await res.json().catch(() => ({}));
      if (!res.ok || !b.ok) throw new Error(b?.error || 'Not reset');
      toast.success('Back to prepared. Nothing was deleted.');
      setCheck(null); onDone();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Not reset'); setCheck(null); }
    finally { setBusy(null); }
  };

  return (
    <span className="flex flex-wrap items-center justify-end gap-2 text-[11px]">
      {check?.state === 'not_filed' && (
        <>
          <span className="text-ink-500">Portal says not filed{check.checkedAt ? ` · checked ${new Date(check.checkedAt).toLocaleTimeString('en-IN')}` : ''}.</span>
          <button onClick={reset} disabled={!!busy}
            className="inline-flex items-center gap-1 rounded-full border border-warn-300 bg-warn-50 px-2.5 py-1 font-semibold text-warn-800 hover:bg-warn-100 disabled:opacity-50">
            {busy === 'reset' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Reset to prepared
          </button>
        </>
      )}
      {check?.state === 'filed' && <span className="text-ink-600">Portal shows it filed{check.arn ? ` · ARN ${check.arn}` : ''}. Open the filing panel to bring it in.</span>}
      {check?.state === 'no_session' && <span className="text-ink-500">Open a portal session to check.</span>}
      {check?.state === 'unknown' && <span className="text-ink-500">The portal could not be asked{check.reason ? `: ${check.reason}` : ''}.</span>}
      <button onClick={ask} disabled={!!busy}
        className="inline-flex items-center gap-1 rounded-full border border-ink-300 bg-paper px-2.5 py-1 font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50">
        {busy === 'check' ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} {check ? 'Check again' : 'Check portal'}
      </button>
    </span>
  );
}
